import { useState } from "react";
import { formatMoneyValue } from "@/app/utils/formatMoneyValue";
import useLinkDetailsController from "./useLinkDetailsController";
import { LinkChart } from "./components/LinkChart";
import { TransactionsList } from "./components/TransactionsList";
import QRCodeModal from "../components/QRCodeModal";
import { ArrowRight } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/view/components/TabsTable";
import { LeadList } from "../components/LeadList";
import { Button } from "@/view/components/ui/button";
import { FormProvider } from "react-hook-form";
import { BasicInput } from "@/view/components/BasicInput";
import { Spinner } from "@/view/components/ui/spinner";
import { UpdateTables } from "../components/UpdateTables";
import DetailsLeadModal from "../components/DetailsLeadModal";

const LinkDetails = () => {
  const [isQRCodeModalOpen, setIsQRCodeModalOpen] = useState(false);

  const {
    linkData,
    simulations,
    chartData,
    totalAccesses,
    allTransactions,
    contracts,
    totalValue,
    isLoading,
    isLoadingTransactions,
    getPageIndex,
    getPageCount,
    handleNextPage,
    handlePreviousPage,
    selectedTab,
    setSelectedTab,
    selectedTables,
    setSelectedTables,
    updateLinkMutation,
    form,
    handleUpdateLink,
    tableOptions,
    isLoadingTables,
    openWhatsApp,
    openLeadDetails,
    closeLeadDetails,
    selectedLeadId,
    isLeadModalOpen,
    linkTables,
  } = useLinkDetailsController();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-lg">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4">
      <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-lg bg-white02 p-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="flex items-end gap-2 text-lg font-semibold text-gray-800">
              <i className="fa-solid fa-chart-simple text-2xl text-primary"></i>{" "}
              Acessos
            </h3>
            <div className="flex items-center gap-2 text-right">
              <p className="text-sm text-black01">Total:</p>
              <p className="text-xl font-bold text-primary">{totalAccesses}</p>
            </div>
          </div>

          <LinkChart data={chartData} isLoading={false} />
        </div>

        <div>
          <button
            onClick={() => setIsQRCodeModalOpen(true)}
            className="mb-6 flex h-12 w-full items-center justify-between gap-2 rounded-lg border border-primary bg-white02 px-4 py-2 font-medium text-primary hover:bg-white02"
          >
            <div className="flex items-center gap-2">
              <i className="fa-solid fa-qrcode text-2xl text-primary"></i>
              Compartilhar link
            </div>
            <ArrowRight className="text-primary" />
          </button>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-lg bg-white02 p-4">
                <p className="mb-1 text-sm text-black01">Simulações</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {simulations}
                </p>
              </div>

              <div className="rounded-lg bg-white02 p-4">
                <p className="mb-1 text-sm text-black01">Contratações</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {contracts}
                </p>
              </div>
            </div>

            <div className="rounded-lg bg-white02 p-4">
              <p className="mb-1 text-sm text-black01">Valor total</p>
              <p className="text-2xl font-semibold text-black01">
                {formatMoneyValue(totalValue)}
              </p>
            </div>

            {linkTables.length > 0 && (
              <div className="rounded-lg bg-white02 p-4">
                <p className="mb-3 text-sm font-medium text-black01">
                  Tabelas e produtos
                </p>

                <div className="flex flex-wrap gap-3">
                  {linkTables.map((table) => (
                    <div
                      key={table.id}
                      className="flex items-center gap-2 rounded-full border border-gray-200 px-3 py-2"
                    >
                      <span className="text-sm font-medium text-gray-700">
                        {table.name}
                      </span>

                      {table.commission !== undefined && (
                        <span className="rounded-full bg-primary px-2 py-0.5 text-xs font-bold text-white">
                          {table.commission}%
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Tabs
        value={selectedTab}
        onValueChange={(value) =>
          setSelectedTab(value as "proposals" | "leads" | "settings")
        }
        className="mt-6"
      >
        <TabsList className="flex items-center gap-8">
          <TabsTrigger value="proposals" className="text-lg">
            Propostas
          </TabsTrigger>
          <TabsTrigger value="leads" className="text-lg">
            Leads
          </TabsTrigger>
          <TabsTrigger value="settings" className="text-lg">
            Configurações
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {selectedTab === "proposals" && (
        <TransactionsList
          transactions={allTransactions}
          isLoading={isLoadingTransactions}
          getPageCount={getPageCount}
          getPageIndex={getPageIndex}
          handleNextPage={handleNextPage}
          handlePreviousPage={handlePreviousPage}
        />
      )}

      {selectedTab === "leads" && (
        <div className="mt-4 flex flex-col gap-4">
          {linkData?.leads?.map((lead, index) => (
            <LeadList
              key={lead._id}
              lead={lead}
              contacted={!!lead.contacted}
              onClick={() => openLeadDetails(lead._id)}
              onContactClick={() =>
                openWhatsApp({
                  _id: lead._id,
                  phone: lead.phone,
                })
              }
              isLast={index === (linkData?.leads?.length || 0) - 1}
            />
          ))}
        </div>
      )}

      {selectedTab === "settings" && (
        <FormProvider {...form}>
          <form onSubmit={handleUpdateLink}>
            <BasicInput
              className="mb-4"
              name="label"
              label="Nome"
              error={form.formState.errors.label?.message}
            />

            <div className="mb-4">
              {isLoadingTables ? (
                <Spinner />
              ) : (
                <UpdateTables
                  options={tableOptions}
                  value={selectedTables}
                  onChange={setSelectedTables}
                />
              )}
            </div>

            <Button
              type="submit"
              isLoading={updateLinkMutation.isPending}
              disabled={updateLinkMutation.isPending}
              className="mt-8 w-[200px] rounded-full font-semibold text-white"
            >
              Alterar configurações
            </Button>
          </form>
        </FormProvider>
      )}

      <QRCodeModal
        isOpen={isQRCodeModalOpen}
        onClose={() => setIsQRCodeModalOpen(false)}
        linkName={linkData?.name || ""}
      />

      <DetailsLeadModal
        visible={isLeadModalOpen}
        onClose={closeLeadDetails}
        leadId={selectedLeadId}
        onContact={(lead) => {
          openWhatsApp({
            _id: lead._id,
            phone: lead.phone,
          });
        }}
      />
    </div>
  );
};

export default LinkDetails;
